import {
  ComputedFieldResourceType,
  computedFieldComputationTypeLabels,
  computedFieldResourceTypeLabels,
  type ComputedFieldComputationType,
} from "@repo/app-types/entities/computedField";
import type { CustomFieldDefinition } from "@repo/app-types/entities/customField";
import {
  CustomFieldProvider,
  CustomFieldType,
} from "@repo/app-types/entities/customField";
import { Input } from "@repo/ui/Inputs/input";
import type { SimpleSelectOption } from "@repo/ui/Select/simpleSelect";
import { SimpleSelect } from "@repo/ui/Select/simpleSelect";
import { useMemo, type FC } from "react";
import { useAllUserCustomFieldDefinitionsQuery } from "../../../api/entities/userCustomField.js";
import { useShouldShowCustomerInteractionResourceType } from "./useShouldShowCustomerInteractionResourceType.js";

const contactResourceTypeOption: SimpleSelectOption<ComputedFieldResourceType> =
  {
    label: computedFieldResourceTypeLabels[ComputedFieldResourceType.CONTACT],
    value: ComputedFieldResourceType.CONTACT,
  };

const customerInteractionResourceTypeOption: SimpleSelectOption<ComputedFieldResourceType> =
  {
    label: computedFieldResourceTypeLabels[ComputedFieldResourceType.GRADING],
    value: ComputedFieldResourceType.GRADING,
  };

const computationTypeOptions: readonly SimpleSelectOption<ComputedFieldComputationType>[] =
  [
    {
      label: computedFieldComputationTypeLabels.count_in,
      value: "count_in",
    },
    {
      label: computedFieldComputationTypeLabels.rate_in,
      value: "rate_in",
    },
  ];

export interface ComputedFieldDefinitionFormProps {
  resourceType: ComputedFieldResourceType;
  displayName: string;
  computationType: ComputedFieldComputationType;
  sourceCustomFieldDefinitionId: string | null;
  values: string;
  numeratorValues: string;
  denominatorValues: string;
  onResourceTypeChange: (value: ComputedFieldResourceType) => void;
  onDisplayNameChange: (value: string) => void;
  onComputationTypeChange: (value: ComputedFieldComputationType) => void;
  onSourceCustomFieldDefinitionIdChange: (value: string) => void;
  onValuesChange: (value: string) => void;
  onNumeratorValuesChange: (value: string) => void;
  onDenominatorValuesChange: (value: string) => void;
}

export const ComputedFieldDefinitionForm: FC<
  ComputedFieldDefinitionFormProps
> = ({
  resourceType,
  displayName,
  computationType,
  sourceCustomFieldDefinitionId,
  values,
  numeratorValues,
  denominatorValues,
  onResourceTypeChange,
  onDisplayNameChange,
  onComputationTypeChange,
  onSourceCustomFieldDefinitionIdChange,
  onValuesChange,
  onNumeratorValuesChange,
  onDenominatorValuesChange,
}) => {
  const { data: customFields } = useAllUserCustomFieldDefinitionsQuery();
  const showCustomerInteractionResourceType =
    useShouldShowCustomerInteractionResourceType();

  const resourceTypeOptions = useMemo<
    readonly SimpleSelectOption<ComputedFieldResourceType>[]
  >(() => {
    const options = [contactResourceTypeOption];
    // Keep the option visible when editing an existing customer
    // interaction computed field even if the gating hook says no.
    if (
      showCustomerInteractionResourceType ||
      resourceType === ComputedFieldResourceType.GRADING
    ) {
      options.push(customerInteractionResourceTypeOption);
    }
    return options;
  }, [showCustomerInteractionResourceType, resourceType]);

  const eligibleSourceFields = useMemo(
    () =>
      (customFields ?? []).filter(
        (field) =>
          field.provider === CustomFieldProvider.User &&
          field.resource_type === resourceType &&
          field.field_type === CustomFieldType.Text,
      ),
    [customFields, resourceType],
  );

  const sourceCustomFieldOptions = useMemo<
    readonly SimpleSelectOption<string>[]
  >(
    () =>
      eligibleSourceFields.map((field: CustomFieldDefinition) => ({
        label: field.display_name,
        value: field.id,
      })),
    [eligibleSourceFields],
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-2">
        <SimpleSelect<ComputedFieldResourceType>
          label="Resource"
          options={resourceTypeOptions}
          value={resourceType}
          onChange={onResourceTypeChange}
          placeholder="Select resource type"
          isDisabled={resourceTypeOptions.length === 1}
        />
        <p className="text-sm text-muted-foreground">
          The type of resource this computed field aggregates over.
        </p>
      </div>

      <Input
        label="Name"
        id="display_name"
        value={displayName}
        onChange={(e) => onDisplayNameChange(e.target.value)}
        placeholder="Qualified opportunity rate"
        description="Name of the computed field. This will be displayed across the product."
        maxLength={200}
      />

      <div className="flex flex-col gap-2">
        <SimpleSelect<ComputedFieldComputationType>
          label="Computation"
          options={computationTypeOptions}
          value={computationType}
          onChange={onComputationTypeChange}
          placeholder="Select computation type"
        />
        <p className="text-sm text-muted-foreground">
          {computationType === "count_in"
            ? "Count of items whose source field value is in the configured set."
            : "Ratio of the count in the numerator set over the count in the denominator set."}
        </p>
      </div>

      <div className="flex flex-col gap-2">
        <SimpleSelect<string>
          label="Source field"
          options={sourceCustomFieldOptions}
          value={sourceCustomFieldDefinitionId}
          onChange={onSourceCustomFieldDefinitionIdChange}
          placeholder={
            sourceCustomFieldOptions.length === 0
              ? "No eligible custom fields"
              : "Select source custom field"
          }
          isDisabled={sourceCustomFieldOptions.length === 0}
        />
        <p className="text-sm text-muted-foreground">
          The user-defined text custom field on the selected resource whose
          values drive this computation.
        </p>
      </div>

      {computationType === "count_in" ? (
        <Input
          label="Values"
          value={values}
          onChange={(e) => onValuesChange(e.target.value)}
          placeholder="qualified, hot, in_progress"
          description="Comma-separated list of values to count. Example: qualified, hot."
          maxLength={100}
        />
      ) : (
        <>
          <Input
            label="Numerator values"
            value={numeratorValues}
            onChange={(e) => onNumeratorValuesChange(e.target.value)}
            placeholder="won"
            description="Comma-separated list of values for the numerator count."
            maxLength={100}
          />
          <Input
            label="Denominator values"
            value={denominatorValues}
            onChange={(e) => onDenominatorValuesChange(e.target.value)}
            placeholder="won, lost"
            description="Comma-separated list of values for the denominator count."
            maxLength={100}
          />
        </>
      )}
    </div>
  );
};

export function parseCommaSeparatedValues(input: string): string[] {
  return input
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}
